import * as z from 'zod';
import {
  TextareaFieldValueSchema,
  HeadlineFieldValueSchema,
  BoardSourceFieldValueSchema,
  ColumnSettingsFieldValueSchema,
  BoardFiltersFieldValueSchema,
  BoardColumnFieldValueSchema,
  SelectFieldValueSchema,
  ItemActionsFieldValueSchema,
  ItemCommentsFieldValueSchema,
  ItemSearchFieldValueSchema,
  DisableAutoFetchFieldValueSchema,
  ItemsPerPageFieldValueSchema,
  HideReloadButtonFieldValueSchema,
  KeyFigureSchema,
} from './schemas';
import type { SectionDescriptor } from './types';

export const TextSectionFieldValuesSchema = z.object({
  headline: HeadlineFieldValueSchema.optional(),
  text: TextareaFieldValueSchema.optional(),
});

export type TextSectionFieldValues = z.infer<typeof TextSectionFieldValuesSchema>;

export const textSectionDescriptor: SectionDescriptor = {
  type: 'text',
  title: 'Text',
  description: 'Display section headings and formatted text content.',
  fields: [
    {
      id: 'headline',
      type: 'headline',
      title: 'Headline',
      defaultValue: { headline: '', level: 1 },
      placeholdersSupport: true,
    },
    {
      id: 'text',
      type: 'textarea',
      title: 'Text',
      placeholdersSupport: true,
    },
  ],
} as const;

export const TeasersSectionFieldValuesSchema = z.object({
  teasers: z
    .array(
      z.object({
        headline: z.string().optional(),
        text: z.string().optional(),
        url: z.string().optional(),
        backgroundColor: z.string().optional(),
      }),
    )
    .optional(),
});

export type TeasersSectionFieldValues = z.infer<typeof TeasersSectionFieldValuesSchema>;

export const teasersSectionDescriptor: SectionDescriptor = {
  type: 'teasers',
  title: 'Teaser',
  description: 'Display link cards to portal pages or external websites.',
  fields: [
    {
      id: 'teasers',
      type: 'teasers',
      title: 'Teaser',
      defaultValue: [
        {
          headline: '',
          text: '',
          url: '',
        },
      ],
    },
  ],
} as const;

export const KeyFiguresSectionFieldValuesSchema = z.object({
  figures: z.array(KeyFigureSchema).optional(),
});

export type KeyFiguresSectionFieldValues = z.infer<typeof KeyFiguresSectionFieldValuesSchema>;

// The section types a key figure can be computed over.
export const KEY_FIGURE_SOURCE_SECTION_TYPES = ['item-table', 'item-grid'];

// A key figure has a value once it names a source section and a function, plus a column unless it counts items.
export const hasKeyFigureSource = (figure: { id?: string; sectionId?: string; columnId?: string; function?: string }) =>
  !!figure.id && !!figure.sectionId && !!figure.function && (figure.function === 'count_items' || !!figure.columnId);

export const keyFiguresSectionDescriptor: SectionDescriptor = {
  type: 'key-figures',
  title: 'Key Figures',
  description: 'Display calculated metrics from a Table or Grid as cards.',
  fields: [
    {
      id: 'figures',
      type: 'key-figures',
      title: 'Key Figures',
      defaultValue: [{ function: 'sum' }],
    },
  ],
} as const;

export const CalloutSectionFieldValuesSchema = z.object({
  headline: z.string().optional(),
  type: SelectFieldValueSchema.optional(),
  icon: SelectFieldValueSchema.optional(),
  text: z.string().optional(),
});

export type CalloutSectionFieldValues = z.infer<typeof CalloutSectionFieldValuesSchema>;

// `type` picks the icon when this is left on 'type' (and for callouts saved before the icon field existed).
export const CALLOUT_ICON_FROM_TYPE = 'type';

// Values are Font Awesome icon names; the portal client renders them as `fa-solid fa-<value>`.
export const calloutIconOptions = [
  { value: 'thumbtack', label: 'Pin' },
  { value: 'circle-info', label: 'Info' },
  { value: 'circle-question', label: 'Question' },
  { value: 'circle-check', label: 'Check' },
  { value: 'hexagon-exclamation', label: 'Exclamation' },
  { value: 'triangle-exclamation', label: 'Warning' },
  { value: 'lightbulb', label: 'Lightbulb' },
  { value: 'phone', label: 'Phone' },
  { value: 'envelope', label: 'Envelope' },
  { value: 'comment', label: 'Comment' },
  { value: 'calendar-days', label: 'Calendar' },
  { value: 'clock', label: 'Clock' },
  { value: 'bell', label: 'Bell' },
  { value: 'star', label: 'Star' },
  { value: 'flag', label: 'Flag' },
  { value: 'file-lines', label: 'Document' },
  { value: 'link', label: 'Link' },
];

// The icon a callout shows when its icon is left on CALLOUT_ICON_FROM_TYPE.
export const getCalloutTypeIcon = (type?: string) => {
  if (type === 'success') {
    return 'circle-check';
  }
  if (type === 'warning' || type === 'error') {
    return 'hexagon-exclamation';
  }
  return 'thumbtack';
};

export const calloutSectionDescriptor: SectionDescriptor = {
  type: 'callout',
  title: 'Callout',
  description: 'Highlight important information in a prominent text box.',
  fields: [
    {
      id: 'type',
      type: 'select',
      variant: 'colored-labels',
      defaultValue: {
        value: 'general',
      },
      options: [
        { value: 'general', label: 'Primary' },
        { value: 'success', label: 'Success' },
        { value: 'warning', label: 'Warning' },
        { value: 'error', label: 'Error' },
      ],
      title: 'Type',
    },
    {
      id: 'icon',
      type: 'select',
      variant: 'icons',
      defaultValue: {
        value: CALLOUT_ICON_FROM_TYPE,
      },
      options: [{ value: CALLOUT_ICON_FROM_TYPE, label: 'Auto-chosen' }, ...calloutIconOptions],
      title: 'Icon',
    },
    {
      id: 'headline',
      type: 'text',
      title: 'Headline',
      placeholdersSupport: true,
    },
    {
      id: 'text',
      type: 'textarea',
      title: 'Text',
      placeholdersSupport: true,
    },
  ],
} as const;

export const EmbedSectionFieldValuesSchema = z.object({
  url: z.string().optional(),
  height: z.number().optional(),
});

export type EmbedSectionFieldValues = z.infer<typeof EmbedSectionFieldValuesSchema>;

export const embedSectionDescriptor: SectionDescriptor = {
  type: 'embed',
  title: 'Embed',
  description: 'Integrate WorkForms or external websites in the portal.',
  fields: [
    {
      // TODO: add url validation
      id: 'url',
      type: 'text',
      title: 'URL',
      placeholder: 'https://',
      placeholdersSupport: true,
    },
    {
      id: 'height',
      type: 'text',
      title: 'Height in pixel',
      placeholder: 'e.g. 500',
      defaultValue: 600,
      inputType: 'number',
    },
  ],
} as const;

export const DetailViewFieldValueSchema = z.object({ enabled: z.boolean().optional() });

export const ItemTableSectionFieldValuesSchema = z.object({
  headline: HeadlineFieldValueSchema.optional(),
  'board-source': BoardSourceFieldValueSchema.optional(),
  'board-filters': BoardFiltersFieldValueSchema.optional(),
  'column-settings': ColumnSettingsFieldValueSchema.optional(),
  'item-actions': ItemActionsFieldValueSchema.optional(),
  'item-comments': ItemCommentsFieldValueSchema.optional(),
  'item-search': ItemSearchFieldValueSchema.optional(),
  'disable-auto-fetch': DisableAutoFetchFieldValueSchema.optional(),
  'hide-reload-button': HideReloadButtonFieldValueSchema.optional(),
  'items-per-page': ItemsPerPageFieldValueSchema.optional(),
  'detail-view': DetailViewFieldValueSchema.optional(),
});

export type ItemTableSectionFieldValues = z.infer<typeof ItemTableSectionFieldValuesSchema>;

export const itemTableSectionDescriptor: SectionDescriptor = {
  type: 'item-table',
  title: 'Board Table',
  description: 'Display items with optional subitems in a table layout.',
  fields: [
    {
      id: 'headline',
      type: 'headline',
      title: 'Headline',
      defaultValue: { headline: '', level: 2 },
      placeholdersSupport: false,
      requiresBoardSource: true,
    },
    {
      id: 'board-source',
      type: 'board-source',
      title: 'Board Source',
    },
    {
      id: 'board-filters',
      type: 'board-filters',
      title: 'Board Filters',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'items-per-page',
      type: 'items-per-page',
      title: 'Items per Page',
      boardSourceFieldId: 'board-source',
      defaultValue: 10,
    },
    {
      id: 'column-settings',
      type: 'column-settings',
      title: 'Column Settings',
      sourceType: 'board-source',
      boardSourceFieldId: 'board-source',
      supportsEditingValues: true,
    },
    {
      id: 'item-actions',
      type: 'item-actions',
      title: 'Item Actions',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'item-search',
      type: 'item-search',
      title: 'Item Search',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'detail-view',
      type: 'detail-view',
      title: 'Item details',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'item-comments',
      type: 'item-comments',
      title: 'Item Comments',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'disable-auto-fetch',
      type: 'disable-auto-fetch',
      title: 'Disable auto fetching',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'hide-reload-button',
      type: 'hide-reload-button',
      title: 'Hide reload button',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'subitems',
      type: 'subitems',
      title: 'Subitems',
      boardSourceFieldId: 'board-source',
    },
  ],
} as const;

export const ItemGridSectionFieldValuesSchema = z.object({
  headline: HeadlineFieldValueSchema.optional(),
  'board-source': BoardSourceFieldValueSchema.optional(),
  'board-filters': BoardFiltersFieldValueSchema.optional(),
  'headline-column': BoardColumnFieldValueSchema.optional(),
  'image-column': BoardColumnFieldValueSchema.optional(),
  'column-settings': ColumnSettingsFieldValueSchema.optional(),
  'item-actions': ItemActionsFieldValueSchema.optional(),
  'item-comments': ItemCommentsFieldValueSchema.optional(),
  'item-search': ItemSearchFieldValueSchema.optional(),
  'disable-auto-fetch': DisableAutoFetchFieldValueSchema.optional(),
  'hide-reload-button': HideReloadButtonFieldValueSchema.optional(),
  'items-per-page': ItemsPerPageFieldValueSchema.optional(),
  'detail-view': DetailViewFieldValueSchema.optional(),
});

export type ItemGridSectionFieldValues = z.infer<typeof ItemGridSectionFieldValuesSchema>;

export const itemGridSectionDescriptor: SectionDescriptor = {
  type: 'item-grid',
  title: 'Board Grid',
  description: 'Display items as visual cards arranged in a grid layout.',
  fields: [
    {
      id: 'headline',
      type: 'headline',
      title: 'Headline',
      defaultValue: { headline: '', level: 2 },
      placeholdersSupport: false,
      requiresBoardSource: true,
    },
    {
      id: 'board-source',
      type: 'board-source',
      title: 'Board Source',
    },
    {
      id: 'board-filters',
      type: 'board-filters',
      title: 'Board Filters',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'items-per-page',
      type: 'items-per-page',
      title: 'Items per Page',
      boardSourceFieldId: 'board-source',
      defaultValue: 10,
    },
    {
      id: 'column-settings',
      type: 'column-settings',
      title: 'Column settings',
      sourceType: 'board-source',
      boardSourceFieldId: 'board-source',
      supportsEditingValues: true,
    },
    {
      id: 'image-column',
      type: 'board-column',
      title: 'Thumbnail',
      icon: 'image',
      description: 'Choose a file column and show its first image as the thumbnail on each card.',
      supportedColumnTypes: ['file'],
      boardSourceFieldId: 'board-source',
      halfWidth: true,
      backgroundNeutral: false,
    },
    {
      id: 'headline-column',
      type: 'board-column',
      title: 'Headline',
      description: 'Choose a text column and show its content as the headline on each card.',
      supportedColumnTypes: ['name', 'text'],
      icon: 'text',
      boardSourceFieldId: 'board-source',
      halfWidth: true,
      backgroundNeutral: false,
    },
    {
      id: 'item-actions',
      type: 'item-actions',
      title: 'Item Actions',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'item-search',
      type: 'item-search',
      title: 'Item Search',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'detail-view',
      type: 'detail-view',
      title: 'Item details',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'item-comments',
      type: 'item-comments',
      title: 'Item Comments',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'disable-auto-fetch',
      type: 'disable-auto-fetch',
      title: 'Disable auto fetching',
      boardSourceFieldId: 'board-source',
    },
    {
      id: 'hide-reload-button',
      type: 'hide-reload-button',
      title: 'Hide reload button',
      boardSourceFieldId: 'board-source',
    },
  ],
} as const;

export const clientItemSummarySectionDescriptor: SectionDescriptor = {
  type: 'client-item-summary',
  title: 'Client Item',
  description: 'Display item columns to the current client in a list layout.',
  fields: [
    {
      id: 'headline',
      type: 'headline',
      title: 'Headline',
      defaultValue: { headline: '', level: 2 },
      placeholdersSupport: false,
    },
    {
      id: 'column-settings',
      type: 'column-settings',
      title: 'Column settings',
      sourceType: 'clients-board',
      supportsEditingValues: true,
    },
  ],
} as const;

export const ClientItemSummaryFieldValuesSchema = z.object({
  headline: HeadlineFieldValueSchema.optional(),
  'column-settings': ColumnSettingsFieldValueSchema.optional(),
});

export type ClientItemSummaryFieldValues = z.infer<typeof ClientItemSummaryFieldValuesSchema>;

export function getSectionDefaultValues(sectionId: string, sectionDescriptor: SectionDescriptor) {
  const fieldValues: Record<string, any> = {};

  sectionDescriptor.fields.forEach((field) => {
    fieldValues[field.id] = 'defaultValue' in field ? field.defaultValue : undefined;
  });

  return {
    type: sectionDescriptor.type,
    id: sectionId,
    fieldValues,
  };
}

export const coreSectionDescriptors = [
  itemGridSectionDescriptor,
  itemTableSectionDescriptor,
  clientItemSummarySectionDescriptor,
  calloutSectionDescriptor,
  textSectionDescriptor,
  embedSectionDescriptor,
  teasersSectionDescriptor,
  keyFiguresSectionDescriptor,
] as const;

export type CoreSectionTypes = SectionDescriptor['type'];

export const coreSectionDescriptorsByType = coreSectionDescriptors.reduce(
  (acc, sectionDescriptor) => {
    acc[sectionDescriptor.type] = sectionDescriptor;
    return acc;
  },
  {} as Record<string, SectionDescriptor>,
);
